import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { approveLoginRequest, declineLoginRequest, peekLoginRequest } from "../../utils/api";
import { Banner, BeeMark, Icon } from "../common/ui";

/**
 * The browser half of «Войти через Google» in the Telegram Mini App
 * (/?approve=<code>): the person is signed in here with Google/Apple and lets
 * the Mini App in as this account. Always an explicit tap, with the check code
 * the Mini App shows — the link might have been sent by someone else.
 */
type Phase = "loading" | "ask" | "busy" | "done" | "declined" | "gone" | "needGoogle" | "conflict";

export default function ApproveLogin({
  code, token, accountName, onClose, onSwitchToGoogle, onToken,
}: {
  /** Merging hands back a session for the merged account (this one is gone). */
  onToken: (accessToken: string) => void;
  code: string;
  token: string;
  accountName: string;
  onClose: () => void;
  onSwitchToGoogle: () => void;
}) {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>("loading");
  const [info, setInfo] = useState<{ check: string; device: string; kind: string; owner: string | null } | null>(null);
  const isLink = info?.kind === "link_google";

  useEffect(() => {
    peekLoginRequest(code, token)
      .then((r) => { setInfo(r); setPhase("ask"); })
      .catch(() => setPhase("gone"));
  }, [code, token]);

  const approve = async () => {
    setPhase("busy");
    try {
      const r = await approveLoginRequest(code, token);
      if (r.access_token) onToken(r.access_token);
      setPhase("done");
    } catch (e) {
      const msg = String(e);
      setPhase(msg.includes("GOOGLE_SESSION_REQUIRED") ? "needGoogle"
        : /ALREADY_LINKED|BOTH_HAVE_/.test(msg) ? "conflict" : "gone");
    }
  };

  const decline = () => {
    declineLoginRequest(code).catch(() => {});
    setPhase("declined");
  };

  return (
    <div className="handoff-confirm">
      <div className="glass glass-lg handoff-confirm__card">
        <div className="row" style={{ gap: 10, marginBottom: 10 }}>
          <BeeMark size={34} />
          <p className="eyebrow" style={{ margin: 0 }}>{t("auth.approveEyebrow")}</p>
        </div>
        {phase === "loading" || phase === "busy" ? (
          <div style={{ display: "grid", placeItems: "center", padding: 24 }}><div className="spinner spinner-amber" /></div>
        ) : phase === "ask" && info ? (
          <>
            <h2 className="t-title" style={{ margin: "0 0 8px" }}>{isLink ? t("auth.linkTitle") : t("auth.approveTitle")}</h2>
            {isLink ? (
              <p className="t-sm" style={{ margin: 0, lineHeight: 1.45 }}>
                {t("auth.linkText", { google: accountName, telegram: info.owner || "—" })}
              </p>
            ) : (
              <p className="t-sm" style={{ margin: 0 }}>{t("auth.approveAs")} <b>{accountName}</b></p>
            )}
            <p className="t-xs muted-2" style={{ margin: "4px 0 12px" }}>{t("auth.approveDevice", { device: info.device })}</p>
            <p className="t-sm" style={{ margin: "0 0 6px" }}>{t("auth.approveCheck")}</p>
            <div className="cross-login__check" style={{ textAlign: "center" }}>{info.check}</div>
            <p className="t-xs muted-2" style={{ margin: "12px 0 14px", lineHeight: 1.45 }}>{t("auth.approveWarn")}</p>
            <button className="btn btn-primary btn-block" onClick={approve}>
              <Icon name="check" size={16} /> {isLink ? t("auth.linkYes") : t("auth.approveYes")}
            </button>
            <button className="btn btn-glass btn-block" style={{ marginTop: 8 }} onClick={decline}>{t("auth.approveNo")}</button>
          </>
        ) : phase === "done" ? (
          <>
            <Banner kind="success">{isLink ? t("auth.linkDone") : t("auth.approveDone")}</Banner>
            <button className="btn btn-glass btn-block" style={{ marginTop: 12 }} onClick={onClose}>{t("pay.doneButton")}</button>
          </>
        ) : phase === "needGoogle" ? (
          <>
            <Banner kind="info">{t("auth.approveNeedGoogle")}</Banner>
            <button className="btn btn-primary btn-block" style={{ marginTop: 12 }} onClick={onSwitchToGoogle}>{t("auth.approveLogout")}</button>
          </>
        ) : (
          <>
            <Banner kind={phase === "declined" ? "info" : "error"}>
              {phase === "declined" ? t("auth.approveDeclined") : phase === "conflict" ? t("linked.errConflict") : t("auth.loginExpired")}
            </Banner>
            <button className="btn btn-glass btn-block" style={{ marginTop: 12 }} onClick={onClose}>{t("pay.doneButton")}</button>
          </>
        )}
      </div>
    </div>
  );
}
